/**
 * Payment Service
 * 
 * Subscription billing APIs. Payment details are collected by Stripe, never by this service.
 */

import { BillingCycle } from '@/types/types';
import { logger, getEnvConfig, PlanType } from 'kmstack-shared/core';
import { apiRequest, APIResponse } from 'kmstack-shared/services';
import { PaymentMethodKind } from '../payment-method.types';


export interface SubscriptionPlanData {
    plan: PlanType;
    billingCycle: BillingCycle;
}

export interface SubscriptionPlanChange {
    currentPlan: PlanType;
    nextPlan: PlanType;
    effectiveAt: string;
    status: 'scheduled' | 'pending_payment' | 'applied';
    paymentUrl?: string;
}

export interface PaymentIntentResponse {
    clientSecret: string;
    paymentIntentId: string;
    status: string;
}

class PaymentService {
    static async setupSavedPaymentMethod(orgId: string, kind: PaymentMethodKind, returnUrl: string, consent: boolean) {
        return this.requireSuccess(await apiRequest(`/${encodeURIComponent(orgId)}/billing/payment-methods/setup`, 'POST', { kind, returnUrl, consent }));
    }

    static async verifySavedPaymentMethod(orgId: string, sessionId: string) {
        return this.requireSuccess(await apiRequest(`/${encodeURIComponent(orgId)}/billing/payment-methods/setup/${encodeURIComponent(sessionId)}/verify`, 'POST'));
    }

    static async setDefaultSavedPaymentMethod(orgId: string, id: string) {
        return this.requireSuccess(await apiRequest(`/${encodeURIComponent(orgId)}/billing/payment-methods/${encodeURIComponent(id)}/default`, 'POST'));
    }

    static async removeSavedPaymentMethod(orgId: string, id: string) {
        return this.requireSuccess(await apiRequest(`/${encodeURIComponent(orgId)}/billing/payment-methods/${encodeURIComponent(id)}`, 'DELETE'));
    }

    private static requireSuccess(response: APIResponse): APIResponse {
        if (response.status < 200 || response.status >= 300) {
            const error = response.error as any;
            throw Object.assign(new Error(typeof error === 'string' ? error : error?.detail || error?.message || 'Unable to update payment methods'), { status: response.status });
        }
        return response;
    }
    /**
     * Initialize Stripe
     * This should be called once when the app loads
     */
    static initializeStripe(): void {
        if (!getEnvConfig().stripePublishableKey) {
            logger.error('Stripe publishable key not configured');
            return;
        }

        // TODO: Initialize Stripe.js
        // const stripe = loadStripe(env.stripePublishableKey);
        logger.dev('Stripe initialized with key:', getEnvConfig().stripePublishableKey);
    }

    /**
     * Initialize PayPal
     * This should be called once when the app loads
     */
    static initializePayPal(): void {
        if (!getEnvConfig().paypalClientId) {
            logger.error('PayPal client ID not configured');
            return;
        }

        // TODO: Initialize PayPal SDK
        logger.dev('PayPal initialized with client ID:', getEnvConfig().paypalClientId);
    }

    /**
     * Get all payment methods for the current user
     * 
     * @returns API response with list of payment methods
     */
    static async getPaymentMethods(orgId: string): Promise<APIResponse> {
        return this.requireSuccess(await apiRequest(`/${ encodeURIComponent(orgId) }/billing/payment-methods`, 'GET'));
    }

    /**
     * Change subscription plan
     * 
     * @param planData - New subscription plan details
     * @returns API response
     */
    static async changeSubscriptionPlan(orgId: string, planData: SubscriptionPlanData): Promise<APIResponse<SubscriptionPlanChange>> {
        return this.requireSuccess(await apiRequest(`/${encodeURIComponent(orgId)}/billing/subscription/change-plan`, 'POST', {
            ...planData,
            billingCycle: planData.billingCycle === BillingCycle.YEARLY ? 'annual' : 'monthly',
        })) as APIResponse<SubscriptionPlanChange>;
    }

    static async getPendingPlanChange(orgId: string): Promise<APIResponse<SubscriptionPlanChange | null>> {
        return this.requireSuccess(await apiRequest(`/${encodeURIComponent(orgId)}/billing/subscription/pending-plan`, 'GET')) as APIResponse<SubscriptionPlanChange | null>;
    }

    /**
     * Cancel subscription
     * 
     * @param immediate - If true, cancel immediately. If false, cancel at end of billing period
     * @returns API response
     */
    static async cancelSubscription(immediate: boolean = false): Promise<APIResponse> {
        logger.dev('Canceling subscription, immediate:', immediate);

        return apiRequest('/billing/subscription/cancel', 'POST', { immediate });
    }

    /**
     * Create payment intent for one-time payment
     * 
     * @param amount - Amount in cents
     * @param currency - Currency code (e.g., 'usd', 'eur')
     * @returns Payment intent details
     */
    static async createPaymentIntent(
        amount: number,
        currency: string = 'usd'
    ): Promise<PaymentIntentResponse> {
        logger.dev('Creating payment intent:', { amount, currency });

        const response = await apiRequest('/billing/create-payment-intent', 'POST', {
            amount,
            currency,
        });

        return response.data as PaymentIntentResponse;
    }

    /**
     * Process PayPal payment
     * 
     * @param orderId - PayPal order ID
     * @returns API response
     */
    static async processPayPalPayment(orderId: string): Promise<APIResponse> {
        logger.dev('Processing PayPal payment:', orderId);

        return apiRequest('/billing/paypal/capture', 'POST', { orderId });
    }

    /**
     * Get billing history
     * 
     * @param page - Page number for pagination
     * @param limit - Number of items per page
     * @returns API response with billing history
     */
    static async getBillingHistory(orgId: string, page: number = 1, limit: number = 10): Promise<APIResponse> {
        return apiRequest(`/${ orgId }/billing/history?page=${ page }&limit=${ limit }`, 'GET');
    }

    /**
     * Download invoice
     * 
     * @param invoiceId - ID of the invoice to download
     * @returns API response with invoice PDF URL
     */
    static async downloadInvoice(invoiceId: string): Promise<APIResponse> {
        logger.dev('Downloading invoice:', invoiceId);

        return apiRequest(`/billing/invoices/${ invoiceId }/download`, 'GET');
    }

    /**
     * Get current subscription details
     * 
     * @returns API response with subscription details
     */
    static async getSubscriptionDetails(orgId: string): Promise<APIResponse> {
        return apiRequest(`/${ orgId }/billing/subscription`, 'GET');
    }

}

export { PaymentService };
